import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import URLs from '../../../../../utils/appURLs';
import { updateToast } from '../../../../../common/redux/reducer/commonSlices/toastSlice';
import axiosInstance from '../../../../../app/config/axiosInstance';
import {
  alertColumns,
  driverIdentificationColumns,
  driverScorecardColumns,
  engineColumns,
  externalVehicleColumns,
  healthColumns,
  idleColumns,
  kmColumns,
  movementColumns,
  overallColumns,
  overspeedColumns,
  parkingColumns,
  stoppageColumns,
  tripColumns,
  tripComplianceColumns,
  vehicleDetailsColumns,
  vehicleGeofenceColumns
} from '../../../pages/reports/components/reportsColumns';
import {
  EpochToDate,
  capitalizeFirstLetter,
  convertEpochToDateTime,
  epochToDateFormatSg
} from '../../../../../utils/commonFunctions';

type ReturnedType = any;

export interface DevicestateProps {
  isLoading: boolean;
  deviceList: string[] | [];
  error: string | null;
}

interface ReportProps {
  deviceID: string;
  dateTime: number;
  timeZone: string;
  driverId: string;
  alertType: string;
  latitude: number;
  longitude: number;
  alertDetails: string;
  vehicleType: string;
  vehicleNumber: string;
  driverID: string;
  startDateTime: number;
  endDateTime: number;
  updateTime: number;
  duration: string;
  imei: number;
  batteryLevel: number;
  driverName?: string;
  networkStrength: string;
  gpsAccuracy: string;
  deviceStatus: string;
  fuelType: string;
  ignitionStatus: string;
  engineOnOffStartTimeStamp: number;
  engineOnOffEndTimeStamp: number;
  engineOnOffStartLatitude: number;
  engineOnOffStartLongitude: number;
  engineOnOffEndLatitude: number;
  engineOnOffEndLongitude: number;
  engineOnOffDuration: string;
  distanceCovered: number;
  avgSpeed: number;
  hikeSpeed: number;
  timezone: string;
  timestamp: number;
  distance: number;
  date: number;
  driverContactNumber: string;
}

export interface Payload {
  report?: string | null;
  startDate?: number | null;
  endDate?: number | null;
  deviceID?: string | null;
  pageno?: number | null;
  pagesize?: number | null;
  toast?: boolean | null;
  signal?: any;
}

//Initial state for reports
export interface ReportstateProps {
  isLoading: boolean;
  error: Object | null;
  rows: any;
  isActive?: [];
  columns: Array<{
    title: string;
    field: string;
    headerName: string;
    flex: number;
    minWidth: number;
  }>;
  count: number | null;
  pdfURL?: null | string;
  excelURL?: null | string;
  externalVehicle?: null | any;
}

const reportState: ReportstateProps = {
  isLoading: false,
  error: null,
  rows: [],
  columns: [],
  count: null,
  pdfURL: null,
  excelURL: null,
  externalVehicle: null
};

interface pdfExcel {
  isLoading: boolean;
  data: any;
  error: string | null;
}
const pdfExcelState: pdfExcel = {
  isLoading: false,
  data: null,
  error: null
};

const deviceState: DevicestateProps = {
  isLoading: false,
  deviceList: [],
  error: null
};

export const getDevices = createAsyncThunk<ReturnedType, any>(
  'devicelist',
  async (signal, thunkApi) => {
    try {
      const response = await axiosInstance({
        method: 'GET',
        url: `${URLs.DEVICE_LIST}`,
        signal: signal
      });
      return response;
    } catch (error: any) {
      if (error?.message !== 'canceled') return thunkApi.rejectWithValue(error);
    }
  }
);

export const deviceListSlice = createSlice({
  name: 'devicelist',
  initialState: deviceState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getDevices.pending, state => {
        state.isLoading = true;
      })
      .addCase(getDevices.fulfilled, (state, action) => {
        if (action?.payload) {
          const {
            data: { data = [] }
          } = action?.payload;
          state.deviceList = data;
        }

        state.isLoading = false;
      })
      .addCase(getDevices.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action.payload?.response?.data?.error || 'Something went wrong';
      });
  }
});

type ThunkArg = any;

export const getPDFExcel = createAsyncThunk<ReturnedType, ThunkArg>(
  'sendmail',
  async (data, thunkApi) => {
    try {
      const response = await axiosInstance({
        method: 'POST',
        url: `${URLs.SEND_MAIL}`,
        data
      });

      if (response) {
        thunkApi.dispatch(
          updateToast({
            show: true,
            message: response?.data?.message,
            severity: 'success'
          })
        );
      }
      return response;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error?.response?.data?.message,
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error);
    }
  }
);

const getPDFExcelSlice = createSlice({
  name: 'sendmail',
  initialState: pdfExcelState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(getPDFExcel.pending, state => {
      state.isLoading = true;
    });
    builder.addCase(getPDFExcel.fulfilled, (state, action) => {
      state.isLoading = false;
      state.data = action.payload;
    });
    builder.addCase(getPDFExcel.rejected, (state, action: any) => {
      state.isLoading = false;
      state.error = action?.payload?.response?.data?.error || 'Network error';
    });
  }
});

// action for km report
export const getKmreport = createAsyncThunk<ReturnedType, Payload>(
  'kmreport',
  async (params, thunkApi) => {
    const { startDate, endDate, deviceID, toast, pageno, pagesize, signal } = params;

    try {
      let URL = `${URLs.KM_REPORT}`;

      if (deviceID) {
        URL += `deviceid=${deviceID}&enddate=${endDate}&startdate=${startDate}&pageno=${pageno}&pagesize=${pagesize}`;
      } else {
        URL += `enddate=${endDate}&startdate=${startDate}&pageno=${pageno}&pagesize=${pagesize}`;
      }
      const response = await axiosInstance({
        method: 'GET',
        url: URL,
        signal: signal
      });
      if (
        response.data.data.kmReportDetails &&
        response.data.data.kmReportDetails.length > 0
      ) {
        thunkApi.dispatch(
          updateToast({
            show: toast,
            severity: 'success',
            message: 'KM Report generated successfully'
          })
        );
      } else {
        thunkApi.dispatch(
          updateToast({
            show: true,
            severity: 'warning',
            message: 'No reports found'
          })
        );
      }

      return response;
    } catch (error: any) {
      if (error?.message !== 'canceled') {
        thunkApi.dispatch(
          updateToast({
            show: true,
            severity: 'error',
            message: error.response?.data?.message
          })
        );
      }

      return thunkApi.rejectWithValue(error);
    }
  }
);

//Reducer for kmreport
export const kmSlice = createSlice({
  name: 'KM',
  initialState: reportState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getKmreport.pending, state => {
        state.isLoading = true;
        state.rows = [];
        state.columns = [];
      })
      .addCase(getKmreport.fulfilled, (state, action: PayloadAction<ReturnedType>) => {
        const {
          data: {
            data: { kmReportDetails, count }
          },
          status
        } = action.payload;

        state.isLoading = false;

        if (status === 200) {
          if (kmReportDetails && kmReportDetails.length > 0) {
            state.rows = kmReportDetails.map((item: ReportProps, index: number) => ({
              ...item,
              id: index + 1,
              date: item?.date,
              vehicleType: capitalizeFirstLetter(item?.vehicleType),
              deviceID: item?.vehicleNumber?.toUpperCase(),
              driverName: capitalizeFirstLetter(
                item?.driverName ? item?.driverName : 'N/A'
              ),
              drivercontact: item?.driverContactNumber,
              distance: item?.distance?.toFixed(2)
            }));

            state.columns = kmColumns;
            state.count = count;
          }
        }
      })

      .addCase(getKmreport.rejected, (state, action: PayloadAction<ReturnedType>) => {
        state.isLoading = false;
        state.error = action.payload?.response?.data?.error ?? 'Something went wrong';
      });
  }
});

//action for stoppage report
export const getStoppagereport = createAsyncThunk<ReturnedType, Payload>(
  'stoppagereport',
  async (params, thunkApi) => {
    const { startDate, endDate, deviceID, toast, pageno, pagesize, signal } = params;

    try {
      let URL = `${URLs.STOPPAGE_REPORT}`;

      if (deviceID) {
        URL += `deviceid=${deviceID}&enddate=${endDate}&startdate=${startDate}&pageno=${pageno}&pagesize=${pagesize}`;
      } else {
        URL += `enddate=${endDate}&startdate=${startDate}&pageno=${pageno}&pagesize=${pagesize}`;
      }
      const response = await axiosInstance({
        method: 'GET',
        url: URL,
        signal: signal
      });
      if (
        response.data.data.stoppageCumulativeDetails &&
        response.data.data.stoppageCumulativeDetails.length > 0
      ) {
        thunkApi.dispatch(
          updateToast({
            show: toast,
            severity: 'success',
            message: 'Stoppage Report generated successfully'
          })
        );
      } else {
        thunkApi.dispatch(
          updateToast({
            show: true,
            severity: 'warning',
            message: 'No reports found'
          })
        );
      }
      return response;
    } catch (error: any) {
      if (error?.message !== 'canceled') {
        thunkApi.dispatch(
          updateToast({
            show: true,
            severity: 'error',
            message: error.response?.data?.message
          })
        );
      }

      return thunkApi.rejectWithValue(error);
    }
  }
);

//Reducer for stoppageReport
export const stoppageSlice = createSlice({
  name: 'Stoppage',
  initialState: reportState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getStoppagereport.pending, state => {
        state.isLoading = true;
        state.rows = [];
        state.columns = [];
      })
      .addCase(getStoppagereport.fulfilled, (state, action) => {
        const {
          data: {
            data: { stoppageCumulativeDetails, count }
          },
          status
        } = action.payload;

        state.isLoading = false;

        if (status === 200) {
          if (stoppageCumulativeDetails && stoppageCumulativeDetails.length > 0) {
            state.rows = stoppageCumulativeDetails.map((item: any, index: number) => ({
              ...item,
              id: index + 1,
              deviceID: item?.deviceID?.toUpperCase(),
              vehicleType: capitalizeFirstLetter(item?.vehicleType),
              driverID: capitalizeFirstLetter(
                item?.driverName ? item?.driverName : 'N/A'
              ),
              count: item?.totalStoppageCount,
              drivercontact: item?.driverContactNumber,
              date: item?.date
            }));
            state.columns = stoppageColumns;
            state.count = count;
          }
        }
      })

      .addCase(getStoppagereport.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action.payload?.response?.data?.error ?? 'Something went wrong';
      });
  }
});

//action for engine report
export const getEnginereport = createAsyncThunk<ReturnedType, Payload>(
  'enginereport',
  async (params, thunkApi) => {
    const { startDate, endDate, deviceID, pageno, pagesize, toast, signal } = params;

    try {
      let URL = `${URLs.ENGINE_REPORT}`;

      if (deviceID) {
        URL += `deviceid=${deviceID}&enddate=${endDate}&startdate=${startDate}&pageno=${pageno}&pagesize=${pagesize}`;
      } else {
        URL += `enddate=${endDate}&startdate=${startDate}&pageno=${pageno}&pagesize=${pagesize}`;
      }
      const response = await axiosInstance({
        method: 'GET',
        url: URL,
        signal: signal
      });
      if (
        response.data.data.engineOnOffData &&
        response.data.data.engineOnOffData.length > 0
      ) {
        thunkApi.dispatch(
          updateToast({
            show: toast,
            severity: 'success',
            message: 'Engine Report generated successfully'
          })
        );
      } else {
        thunkApi.dispatch(
          updateToast({
            show: true,
            severity: 'warning',
            message: 'No reports found'
          })
        );
      }
      return response;
    } catch (error: any) {
      if (error?.message !== 'canceled') {
        thunkApi.dispatch(
          updateToast({
            show: true,
            severity: 'error',
            message: error.response?.data?.message
          })
        );
      }

      return thunkApi.rejectWithValue(error);
    }
  }
);

// reducer for engine report
export const engineSlice = createSlice({
  name: 'Engine',
  initialState: reportState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getEnginereport.pending, state => {
        state.isLoading = true;
        state.rows = [];
        state.columns = [];
      })
      .addCase(
        getEnginereport.fulfilled,
        (state, action: PayloadAction<ReturnedType>) => {
          const {
            data: {
              data: { engineOnOffData, count, timeZone }
            },
            status
          } = action.payload;

          state.isLoading = false;

          if (status === 200) {
            if (engineOnOffData && engineOnOffData.length > 0) {
              state.rows = engineOnOffData.map((item: ReportProps, index: number) => ({
                ...item,
                id: index + 1,
                deviceID: item?.vehicleNumber?.toUpperCase(),
                vehicleType: capitalizeFirstLetter(item?.vehicleType),
                engineType: item?.fuelType,
                ignitionStatus: item?.ignitionStatus,
                drivercontact: item?.driverContactNumber,
                startTime: convertEpochToDateTime(
                  item?.engineOnOffStartTimeStamp,
                  timeZone
                ),
                endTime: convertEpochToDateTime(item?.engineOnOffEndTimeStamp, timeZone),
                startLat: item?.engineOnOffStartLatitude,
                startLng: item?.engineOnOffStartLongitude,
                endLat: item?.engineOnOffEndLatitude,
                endLng: item?.engineOnOffEndLongitude,
                duration: item?.engineOnOffDuration,
                distance: item?.distanceCovered?.toFixed(2),
                averageSpeed: item?.avgSpeed?.toFixed(2),
                hikeSpeed: item?.hikeSpeed?.toFixed(2),
                type: 'Engine'
              }));
              state.columns = engineColumns;
              state.count = count;
            }
          }
        }
      )
      .addCase(getEnginereport.rejected, (state, action: PayloadAction<ReturnedType>) => {
        state.isLoading = false;
        state.error = action.payload?.response?.data?.error ?? 'Something went wrong';
      });
  }
});

//action for idle report
export const getIdlereport = createAsyncThunk<ReturnedType, Payload>(
  'idlereport',
  async (params, thunkApi) => {
    const { startDate, endDate, deviceID, pageno, pagesize, toast, signal } = params;

    try {
      let URL = `${URLs.IDLE_REPORT}`;

      if (deviceID) {
        URL += `deviceid=${deviceID}&enddate=${endDate}&startdate=${startDate}&pageno=${pageno}&pagesize=${pagesize}`;
      } else {
        URL += `enddate=${endDate}&startdate=${startDate}&pageno=${pageno}&pagesize=${pagesize}`;
      }
      const response = await axiosInstance({
        method: 'GET',
        url: URL,
        signal: signal
      });
      if (
        response.data.data.idleReportInfo &&
        response.data.data.idleReportInfo.length > 0
      ) {
        thunkApi.dispatch(
          updateToast({
            show: toast,
            severity: 'success',
            message: 'Idle Report generated successfully'
          })
        );
      } else {
        thunkApi.dispatch(
          updateToast({
            show: true,
            severity: 'warning',
            message: 'No reports found'
          })
        );
      }
      return response;
    } catch (error: any) {
      if (error?.message !== 'canceled') {
        thunkApi.dispatch(
          updateToast({
            show: true,
            severity: 'error',
            message: error.response?.data?.message
          })
        );
      }

      return thunkApi.rejectWithValue(error);
    }
  }
);

// reducer for idle report
export const idleSlice = createSlice({
  name: 'Idle',
  initialState: reportState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getIdlereport.pending, state => {
        state.isLoading = true;
        state.rows = [];
        state.columns = [];
      })
      .addCase(getIdlereport.fulfilled, (state, action: PayloadAction<ReturnedType>) => {
        const {
          data: {
            data: { idleReportInfo, idleTotalCount, timeZone }
          },
          status
        } = action.payload;

        state.isLoading = false;

        if (status === 200) {
          if (idleReportInfo && idleReportInfo.length > 0) {
            state.rows = idleReportInfo.map((item: ReportProps, index: number) => ({
              ...item,
              id: index + 1,
              deviceID: item?.vehicleNumber?.toUpperCase(),
              vehicleType: capitalizeFirstLetter(item?.vehicleType),
              driver: item?.driverName ? capitalizeFirstLetter(item?.driverName) : 'N/A',
              startTime: convertEpochToDateTime(item?.startDateTime, timeZone),
              endTime: convertEpochToDateTime(item?.endDateTime, timeZone),
              drivercontact: item?.driverContactNumber,
              updateTime: convertEpochToDateTime(
                item?.updateTime,
                item?.timeZone === null ? 'Asia/Kolkata' : item?.timeZone
              ),
              duration: item?.duration,
              lat: item?.latitude,
              lng: item?.longitude,
              type: 'Idle'
            }));
            state.columns = idleColumns;
            state.count = idleTotalCount;
          }
        }
      })
      .addCase(getIdlereport.rejected, (state, action: PayloadAction<ReturnedType>) => {
        state.isLoading = false;
        state.error = action.payload?.response?.data?.error ?? 'Something went wrong';
      });
  }
});

//action for movement report
export const getMovementreport = createAsyncThunk<ReturnedType, Payload>(
  'movementreport',
  async (params, thunkApi) => {
    const { startDate, endDate, deviceID, pageno, pagesize, toast, signal } = params;

    try {
      let URL = `${URLs.MOVEMENT_REPORT}`;

      if (deviceID) {
        URL += `deviceid=${deviceID}&enddate=${endDate}&startdate=${startDate}&pageno=${pageno}&pagesize=${pagesize}`;
      } else {
        URL += `enddate=${endDate}&startdate=${startDate}&pageno=${pageno}&pagesize=${pagesize}`;
      }
      const response = await axiosInstance({
        method: 'GET',
        url: URL,
        signal: signal
      });
      if (
        response.data.data.movementReportInfo &&
        response.data.data.movementReportInfo.length > 0
      ) {
        thunkApi.dispatch(
          updateToast({
            show: toast,
            severity: 'success',
            message: 'Movement Report generated successfully'
          })
        );
      } else {
        thunkApi.dispatch(
          updateToast({
            show: true,
            severity: 'warning',
            message: 'No reports found'
          })
        );
      }
      return response;
    } catch (error: any) {
      if (error?.message !== 'canceled') {
        thunkApi.dispatch(
          updateToast({
            show: true,
            severity: 'error',
            message: error.response?.data?.message
          })
        );
      }

      return thunkApi.rejectWithValue(error);
    }
  }
);

// reducer for movement report
export const movementSlice = createSlice({
  name: 'Movement',
  initialState: reportState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getMovementreport.pending, state => {
        state.isLoading = true;
        state.rows = [];
        state.columns = [];
      })
      .addCase(getMovementreport.fulfilled, (state, action) => {
        const {
          data: {
            data: { movementReportInfo, count, timeZone }
          },
          status
        } = action.payload;

        state.isLoading = false;

        if (status === 200) {
          if (movementReportInfo && movementReportInfo.length > 0) {
            state.rows = movementReportInfo.map((item: any, index: number) => ({
              ...item,
              id: index + 1,
              deviceID: item?.vehicleNumber?.toUpperCase(),
              vehicleType: capitalizeFirstLetter(item?.vehicleType),
              driver: capitalizeFirstLetter(item?.driverName ? item?.driverName : 'N/A'),
              startTime: convertEpochToDateTime(item?.movementStartDateTime, timeZone),
              endTime: convertEpochToDateTime(item?.movementEndDateTime, timeZone),
              duration: item?.movementDuration,
              averageSpeed: item?.avgSpeed?.toFixed(2),
              hikeSpeed: item?.hikeSpeed?.toFixed(2),
              startLat: item?.movementStartLatitude,
              startLng: item?.movementStartLongitude,
              drivercontact: item?.driverContactNumber,
              endLat: item?.movementEndLatitude,
              endLng: item?.movementEndLongitude,
              type: 'Movement'
            }));
            state.columns = movementColumns;
            state.count = count;
          }
        }
      })
      .addCase(getMovementreport.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action.payload?.response?.data?.error ?? 'Something went wrong';
      });
  }
});

//action for parking report
export const getParkingreport = createAsyncThunk<ReturnedType, Payload>(
  'parkingreport',
  async (params, thunkApi) => {
    const { startDate, endDate, deviceID, pageno, pagesize, toast, signal } = params;

    try {
      let URL = `${URLs.PARKING_REPORT}`;

      if (deviceID) {
        URL += `deviceid=${deviceID}&enddate=${endDate}&startdate=${startDate}&pageno=${pageno}&pagesize=${pagesize}`;
      } else {
        URL += `enddate=${endDate}&startdate=${startDate}&pageno=${pageno}&pagesize=${pagesize}`;
      }

      const response = await axiosInstance({
        method: 'GET',
        url: URL,
        signal: signal
      });

      if (
        response.data.data.parkingReport &&
        response.data.data.parkingReport.length > 0
      ) {
        thunkApi.dispatch(
          updateToast({
            show: toast,
            severity: 'success',
            message: 'Parking Report generated successfully'
          })
        );
      } else {
        thunkApi.dispatch(
          updateToast({
            show: true,
            severity: 'warning',
            message: 'No reports found'
          })
        );
      }
      return response;
    } catch (error: any) {
      if (error?.message !== 'canceled') {
        thunkApi.dispatch(
          updateToast({
            show: true,
            severity: 'error',
            message: error.response?.data?.message
          })
        );
      }

      return thunkApi.rejectWithValue(error);
    }
  }
);

// reducer for parking report
export const parkingSlice = createSlice({
  name: 'Parking',
  initialState: reportState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getParkingreport.pending, state => {
        state.isLoading = true;
        state.rows = [];
        state.columns = [];
      })
      .addCase(getParkingreport.fulfilled, (state, action) => {
        const {
          data: {
            data: { parkingReport, count, timeZone }
          },
          status
        } = action.payload;

        state.isLoading = false;

        if (status === 200) {
          if (parkingReport && parkingReport.length > 0) {
            state.rows = parkingReport.map((item: any, index: number) => ({
              ...item,
              id: index + 1,
              deviceID: item?.vehicleNumber?.toUpperCase(),
              driverName: item?.driverName
                ? capitalizeFirstLetter(item?.driverName)
                : 'N/A',
              vehicleType: capitalizeFirstLetter(item?.vehicleType),
              startTime: convertEpochToDateTime(item?.startTime, timeZone),
              endTime: convertEpochToDateTime(item?.endTime, timeZone),
              drivercontact: item?.driverContactNumber,
              updateTime: convertEpochToDateTime(
                item?.updateTime,
                item?.timeZone === null ? 'Asia/Kolkata' : item?.timeZone
              ),
              duration: item?.duration,
              lat: item?.latitude,
              lng: item?.longitude,
              type: 'Parking'
            }));
            state.columns = parkingColumns;
            state.count = count;
          }
        }
      })
      .addCase(getParkingreport.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action.payload?.response?.data?.error ?? 'Something went wrong';
      });
  }
});

//action for overall report
export const getOverallreport = createAsyncThunk<ReturnedType, Payload>(
  'overallreport',
  async (params, thunkApi) => {
    const { startDate, endDate, deviceID, pageno, pagesize, toast, signal } = params;
    try {
      let URL = `${URLs.OVERALL_REPORT}`;

      if (deviceID) {
        URL += `deviceid=${deviceID}&enddate=${endDate}&startdate=${startDate}&pageno=${pageno}&pagesize=${pagesize}`;
      } else {
        URL += `enddate=${endDate}&startdate=${startDate}&pageno=${pageno}&pagesize=${pagesize}`;
      }
      const response = await axiosInstance({
        method: 'GET',
        url: URL,
        signal: signal
      });
      if (
        response.data.data.overAllReportInfo &&
        response.data.data.overAllReportInfo.length > 0
      ) {
        thunkApi.dispatch(
          updateToast({
            show: toast,
            severity: 'success',
            message: 'Overall Report generated successfully'
          })
        );
      } else {
        thunkApi.dispatch(
          updateToast({
            show: true,
            severity: 'warning',
            message: 'No reports found'
          })
        );
      }
      return response;
    } catch (error: any) {
      if (error?.message !== 'canceled') {
        thunkApi.dispatch(
          updateToast({
            show: true,
            severity: 'error',
            message: error.response?.data?.message
          })
        );
      }
      return thunkApi.rejectWithValue(error);
    }
  }
);

// reducer for overall report
export const overallSlice = createSlice({
  name: 'Overall',
  initialState: reportState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getOverallreport.pending, state => {
        state.isLoading = true;
        state.rows = [];
        state.columns = [];
      })
      .addCase(getOverallreport.fulfilled, (state, action) => {
        const {
          data: {
            data: { overAllReportInfo, count, timeZone }
          },
          status
        } = action.payload;

        state.isLoading = false;

        if (status === 200) {
          if (overAllReportInfo && overAllReportInfo.length > 0) {
            state.rows = overAllReportInfo.map((item: any, index: number) => ({
              ...item,
              id: index + 1,
              deviceID: item?.vehicleNumber?.toUpperCase(),
              vehicleType: capitalizeFirstLetter(item?.vehicleType),
              vehicleStatus: item?.vehicleStatus,
              driver: capitalizeFirstLetter(item?.driverName ? item?.driverName : 'N/A'),
              startTime: convertEpochToDateTime(item?.startDateAndTime, timeZone),
              endTime: convertEpochToDateTime(item?.endDateAndTime, timeZone),
              duration: item?.duration,
              averagespeed: item?.averageSpeed,
              drivercontact: item?.driverContactNumber,
              hikeSpeed: item?.hikeSpeed,
              distanceTravelled: item?.distanceTravelled
            }));
            state.columns = overallColumns;
            state.count = count;
          }
        }
      })
      .addCase(getOverallreport.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action.payload?.response?.data?.error ?? 'Something went wrong';
      });
  }
});

//action for overspeed report
export const getOverspeedreport = createAsyncThunk<ReturnedType, Payload>(
  'overspeedreport',
  async (params, thunkApi) => {
    const { startDate, endDate, deviceID, pageno, pagesize, toast, signal } = params;
    try {
      let URL = `${URLs.OVERSPEED_REPORT}`;

      if (deviceID) {
        URL += `deviceid=${deviceID}&enddate=${endDate}&startdate=${startDate}&pageno=${pageno}&pagesize=${pagesize}`;
      } else {
        URL += `enddate=${endDate}&startdate=${startDate}&pageno=${pageno}&pagesize=${pagesize}`;
      }
      const response = await axiosInstance({
        method: 'GET',
        url: URL,
        signal: signal
      });
      if (
        response.data.data.overSpeedDeviceReportInfo &&
        response.data.data.overSpeedDeviceReportInfo.length > 0
      ) {
        thunkApi.dispatch(
          updateToast({
            show: toast,
            severity: 'success',
            message: 'Overspeed Report generated successfully'
          })
        );
      } else {
        thunkApi.dispatch(
          updateToast({
            show: true,
            severity: 'warning',
            message: 'No reports found'
          })
        );
      }

      return response;
    } catch (error: any) {
      if (error?.message !== 'canceled') {
        thunkApi.dispatch(
          updateToast({
            show: true,
            severity: 'error',
            message: error.response?.data?.message
          })
        );
      }

      return thunkApi.rejectWithValue(error);
    }
  }
);

// reducer for overspeed report
export const overspeedSlice = createSlice({
  name: 'Overspeed',
  initialState: reportState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getOverspeedreport.pending, state => {
        state.isLoading = true;
        state.rows = [];
        state.columns = [];
      })
      .addCase(getOverspeedreport.fulfilled, (state, action) => {
        const {
          data: {
            data: { overSpeedDeviceReportInfo, count, timeZone }
          },
          status
        } = action.payload;

        state.isLoading = false;

        if (status === 200) {
          if (overSpeedDeviceReportInfo && overSpeedDeviceReportInfo.length > 0) {
            state.rows = overSpeedDeviceReportInfo.map((item: any, index: number) => ({
              ...item,
              id: index + 1,
              deviceID: item?.vehicleNumber?.toUpperCase(),
              vehicleType: capitalizeFirstLetter(item?.vehicleType),
              driver: item?.driverName ? capitalizeFirstLetter(item?.driverName) : 'N/A',
              startTime: convertEpochToDateTime(item?.overSpeedStartTime, timeZone),
              endTime: convertEpochToDateTime(item?.overSpeedEndTime, timeZone),
              duration: item?.overSpeedDuration,
              distance: item?.distance?.toFixed(2),
              drivercontact: item?.driverContactNumber,
              averageSpeed: item?.hikeSpeed?.toFixed(2),
              overspeed: item?.speedLimit?.toFixed(2),
              roadType: item?.roadType ?? 'N/A'
            }));
            state.columns = overspeedColumns;
            state.count = count;
          }
        }
      })
      .addCase(getOverspeedreport.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action.payload?.response?.data?.error ?? 'Something went wrong';
      });
  }
});

//action for alert report
export const getAlertreport = createAsyncThunk<ReturnedType, Payload>(
  'alertreport',
  async (params, thunkApi) => {
    const { startDate, endDate, deviceID, pageno, pagesize, toast, signal } = params;
    try {
      let URL = `${URLs.ALERT_NOTIFICATION_REPORT}`;

      if (deviceID) {
        URL += `deviceid=${deviceID}&enddate=${endDate}&startdate=${startDate}&pageno=${pageno}&pagesize=${pagesize}`;
      } else {
        URL += `enddate=${endDate}&startdate=${startDate}&pageno=${pageno}&pagesize=${pagesize}`;
      }
      const response = await axiosInstance({
        method: 'GET',
        url: URL,
        signal: signal
      });

      if (
        response.data.data.alertReportData &&
        response.data.data.alertReportData.length > 0
      ) {
        thunkApi.dispatch(
          updateToast({
            show: toast,
            severity: 'success',
            message: 'Alert Report generated successfully'
          })
        );
      } else {
        thunkApi.dispatch(
          updateToast({
            show: true,
            severity: 'warning',
            message: 'No reports found'
          })
        );
      }
      return response;
    } catch (error: any) {
      if (error?.message !== 'canceled') {
        thunkApi.dispatch(
          updateToast({
            show: true,
            severity: 'error',
            message: error.response?.data?.message
          })
        );
      }

      return thunkApi.rejectWithValue(error);
    }
  }
);

// reducer for alert report
export const alertSlice = createSlice({
  name: 'Alert',
  initialState: reportState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getAlertreport.pending, state => {
        state.isLoading = true;
        state.rows = [];
        state.columns = [];
      })
      .addCase(getAlertreport.fulfilled, (state, action: PayloadAction<ReturnedType>) => {
        const {
          data: {
            data: { alertReportData, count, timeZone }
          },
          status
        } = action.payload;

        state.isLoading = false;

        if (status === 200) {
          if (alertReportData && alertReportData.length > 0) {
            state.rows = alertReportData.map((item: ReportProps, index: number) => ({
              ...item,
              id: index + 1,
              deviceID: item?.deviceID?.toUpperCase(),
              date: convertEpochToDateTime(item?.dateTime, timeZone),
              driver: item?.driverName ? capitalizeFirstLetter(item?.driverName) : 'N/A',
              drivercontact: item?.driverContactNumber,
              vehicleType: capitalizeFirstLetter(item?.vehicleType),
              alertType: item?.alertType,
              lat: item?.latitude,
              lng: item?.longitude,
              alertDetails:
                item?.alertDetails === null || item?.alertDetails === ''
                  ? 'N/A'
                  : item?.alertDetails
            }));
            state.columns = alertColumns;
            state.count = count;
          }
        }
      })
      .addCase(getAlertreport.rejected, (state, action: PayloadAction<ReturnedType>) => {
        state.isLoading = false;
        state.error = action.payload?.response?.data?.error ?? 'Something went wrong';
      });
  }
});

//action for detailed trip report
export const getDetailedTripreport = createAsyncThunk<ReturnedType, Payload>(
  'detailedtripreport',
  async (params, thunkApi) => {
    const { startDate, endDate, deviceID, toast, pageno, pagesize, signal } = params;
    try {
      let URL = `${URLs.DETAILED_TRIP_REPORT}`;

      if (deviceID) {
        URL += `deviceid=${deviceID}&enddate=${endDate}&startdate=${startDate}&pageno=${pageno}&pagesize=${pagesize}`;
      } else {
        URL += `enddate=${endDate}&startdate=${startDate}&pageno=${pageno}&pagesize=${pagesize}`;
      }
      const response = await axiosInstance({
        method: 'GET',
        url: URL,
        signal: signal
      });

      if (
        response.data.data.tripDetailReportResponse &&
        response.data.data.tripDetailReportResponse.length > 0
      ) {
        thunkApi.dispatch(
          updateToast({
            show: toast,
            severity: 'success',
            message: 'Trip Details Report generated successfully'
          })
        );
      } else {
        thunkApi.dispatch(
          updateToast({
            show: true,
            severity: 'warning',
            message: 'No reports found'
          })
        );
      }
      return response;
    } catch (error: any) {
      if (error?.message !== 'canceled') {
        thunkApi.dispatch(
          updateToast({
            show: true,
            severity: 'error',
            message: error.response?.data?.message
          })
        );
      }
      return thunkApi.rejectWithValue(error);
    }
  }
);

// reducer for detailed trip report
export const detailedTripSlice = createSlice({
  name: 'TripDetails',
  initialState: reportState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getDetailedTripreport.pending, state => {
        state.isLoading = true;
        state.rows = [];
        state.columns = [];
      })
      .addCase(getDetailedTripreport.fulfilled, (state, action) => {
        const {
          data: {
            data: { tripDetailReportResponse, count, timezone }
          },
          status
        } = action.payload;

        state.isLoading = false;

        if (status === 200) {
          if (tripDetailReportResponse && tripDetailReportResponse.length > 0) {
            state.rows = tripDetailReportResponse.map((item: any, index: number) => ({
              ...item,
              id: index + 1,
              deviceID: item?.vehicleNumber?.toUpperCase(),
              vehicleType: item?.vehicleType,
              driver: item?.driverName ? capitalizeFirstLetter(item?.driverName) : 'N/A',
              driverName: item?.driverName,
              tripID: item?.tripId,
              drivercontact: item?.driverContactNumber,
              actualStartTime: convertEpochToDateTime(
                item?.actualTripStartTime,
                timezone
              ),
              actualEndTime: convertEpochToDateTime(item?.actualTripEndTime, timezone),
              actualDuration: item?.actualDuration,
              actualDistance: item?.actualDistance.toFixed(2),
              assignedStartTime: convertEpochToDateTime(
                item?.assignedTripStartTime,
                timezone
              ),
              assignedEndTime: convertEpochToDateTime(
                item?.assignedTripEndTime,
                timezone
              ),
              estimatedDuration: item?.assignedEstimatedDuration,
              estimatedDistance: item?.assignedEstimatedDistance.toFixed(2),
              source: item?.tripStartLocation,
              destination: item?.tripEndLocation,
              waypoints: item?.tripWayPointsDetails,
              idleDuration: item?.tripIdleDuration,
              parkingDuration: item?.tripParkingDuration,
              drivingDuration: item?.tripDrivingDuration,
              stoppageCount: item?.tripStoppageCount,
              tripCompliance: item?.overAllTripCompliancePercentage,
              plannedEncodedPolyline: item?.plannedEncodedPolyline,
              completedWaypoints: item?.tripComplianceDTO?.noOfCompletedWayPoints,
              incompleteWaypoiNts: item?.tripComplianceDTO?.noOfInCompleteWayPoints,
              actualRouteEncodedPolyline: item?.actualRouteEncodedPolyline,
              type: 'TripDetails',
              timezone: timezone
            }));

            state.columns = tripColumns;
            state.count = count;
          }
        }
      })
      .addCase(getDetailedTripreport.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action.payload?.response?.data?.error ?? 'Something went wrong';
      });
  }
});

type Thunk = boolean;

//action for device health report
export const getHealthreport = createAsyncThunk<ReturnedType, Payload>(
  'devicehealthreport',
  async (params, thunkApi) => {
    const { toast, pageno, pagesize, signal } = params;
    try {
      let URL = `${URLs.DEVICE_HEALTH_REPORT}&pageno=${pageno}&pagesize=${pagesize}`;

      const response = await axiosInstance({
        method: 'GET',
        url: URL,
        signal: signal
      });
      if (
        response.data.data.deviceHealthReport &&
        response.data.data.deviceHealthReport.length > 0
      ) {
        thunkApi.dispatch(
          updateToast({
            show: toast,
            severity: 'success',
            message: 'Device Health Report generated successfully'
          })
        );
      } else {
        thunkApi.dispatch(
          updateToast({
            show: true,
            severity: 'warning',
            message: 'No reports found'
          })
        );
      }
      return response;
    } catch (error: any) {
      if (error?.message !== 'canceled') {
        thunkApi.dispatch(
          updateToast({
            show: true,
            severity: 'error',
            message: error.response?.data?.message
          })
        );
      }

      return thunkApi.rejectWithValue(error);
    }
  }
);

// reducer for  device health report
export const deviceHealthSlice = createSlice({
  name: 'DeviceHealth',
  initialState: reportState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getHealthreport.pending, state => {
        state.isLoading = true;
        state.rows = [];
        state.columns = [];
      })
      .addCase(
        getHealthreport.fulfilled,
        (state, action: PayloadAction<ReturnedType>) => {
          const {
            data: {
              data: { deviceHealthReport, count }
            },
            status
          } = action.payload;

          state.isLoading = false;

          if (status === 200) {
            if (deviceHealthReport && deviceHealthReport.length > 0) {
              state.rows = deviceHealthReport.map((item: ReportProps, index: number) => ({
                ...item,
                id: index + 1,
                deviceID: item?.vehicleNumber?.toUpperCase(),
                vehicleType: capitalizeFirstLetter(item?.vehicleType),
                imei: item?.imei,
                batteryLevel: item?.batteryLevel,
                network: item?.networkStrength,
                gps: item?.gpsAccuracy === 'none' ? 'N/A' : item?.gpsAccuracy,
                deviceStatus: item?.deviceStatus
              }));
              state.count = count;
              state.columns = healthColumns;
            }
          }
        }
      )
      .addCase(getHealthreport.rejected, (state, action: PayloadAction<ReturnedType>) => {
        state.isLoading = false;
        state.error = action.payload?.response?.data?.error ?? 'Something went wrong';
      });
  }
});
//action for vehicle geofence report
export const getVehicleGeofencereport = createAsyncThunk<ReturnedType, Payload>(
  'vehicleGeofenceReport',
  async (params, thunkApi) => {
    const { startDate, endDate, deviceID, toast, pageno, pagesize, signal } = params;
    try {
      let URL = `${URLs.VEHICLE_GEOFENCE}`;

      if (deviceID) {
        URL += `deviceid=${deviceID}&enddate=${endDate}&startdate=${startDate}&pageno=${pageno}&pagesize=${pagesize}`;
      } else {
        URL += `enddate=${endDate}&startdate=${startDate}&pageno=${pageno}&pagesize=${pagesize}`;
      }
      const response = await axiosInstance({
        method: 'GET',
        url: URL,
        signal: signal
      });
      if (
        response.data.data.geoZoneReportResponse &&
        response.data.data.geoZoneReportResponse.length > 0
      ) {
        thunkApi.dispatch(
          updateToast({
            show: toast,
            severity: 'success',
            message: 'Vehicle Geofence Report generated successfully'
          })
        );
      } else {
        thunkApi.dispatch(
          updateToast({
            show: true,
            severity: 'warning',
            message: 'No reports found'
          })
        );
      }
      return response;
    } catch (error: any) {
      if (error?.message !== 'canceled') {
        thunkApi.dispatch(
          updateToast({
            show: true,
            severity: 'error',
            message: error.response?.data?.message
          })
        );
      }
      return thunkApi.rejectWithValue(error);
    }
  }
);

// reducer for vehicle geofence report
export const vehicleGeofenceSlice = createSlice({
  name: 'VehicleGeofence',
  initialState: reportState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getVehicleGeofencereport.pending, state => {
        state.isLoading = true;
        state.rows = [];
        state.columns = [];
      })
      .addCase(getVehicleGeofencereport.fulfilled, (state, action) => {
        const {
          data: {
            data: { geoZoneReportResponse, count, timeZone }
          },
          status
        } = action.payload;

        state.isLoading = false;

        if (status === 200) {
          if (geoZoneReportResponse && geoZoneReportResponse.length > 0) {
            state.rows = geoZoneReportResponse.map((item: any, index: number) => ({
              ...item,
              id: index + 1,
              vehicleNumber: item?.vehicleNumber.toUpperCase(),
              vehicleType: item?.vehicleType,
              geoZoneId: item?.geoZoneId,
              geozoneName: item?.geoZoneName,
              geoZoneType: item?.geoZoneType,
              driver: item?.driverName ? capitalizeFirstLetter(item?.driverName) : 'N/A',
              drivercontact: item?.driverContactNumber,
              inTime: convertEpochToDateTime(item?.geoZoneInDateTime, timeZone),
              outTime: convertEpochToDateTime(item?.geoZoneOutDateTime, timeZone),
              duration: item?.durationIn,
              distance: item?.distanceTravelled,
              radius: item?.geoZoneRadius.toFixed(2)
            }));
            state.columns = vehicleGeofenceColumns;
          }
        }
        state.count = count;
      })
      .addCase(getVehicleGeofencereport.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action.payload?.response?.data?.error ?? 'Something went wrong';
      });
  }
});

//action for login history report
export const getVehicleDetails = createAsyncThunk<ReturnedType, Payload>(
  'vehicledetails',
  async (params, thunkApi) => {
    const { startDate, endDate, deviceID, toast, pageno, pagesize, signal } = params;
    try {
      let URL = `${URLs.VEHICLE_DETAILS}`;

      if (deviceID) {
        URL += `deviceid=${deviceID}&enddate=${endDate}&startdate=${startDate}&pageno=${pageno}&pagesize=${pagesize}`;
      } else {
        URL += `enddate=${endDate}&startdate=${startDate}&pageno=${pageno}&pagesize=${pagesize}`;
      }

      const response = await axiosInstance({
        method: 'GET',
        url: URL,
        signal: signal
      });
      if (
        response.data.data.vehicleDetailReport &&
        response.data.data.vehicleDetailReport.length > 0
      ) {
        thunkApi.dispatch(
          updateToast({
            show: toast,
            severity: 'success',
            message: 'Vehicle Details Report generated successfully'
          })
        );
      } else {
        thunkApi.dispatch(
          updateToast({
            show: true,
            severity: 'warning',
            message: 'No reports found'
          })
        );
      }
      return response;
    } catch (error: any) {
      if (error?.message !== 'canceled') {
        thunkApi.dispatch(
          updateToast({
            show: true,
            severity: 'error',
            message: error.response?.data?.message
          })
        );
      }
      return thunkApi.rejectWithValue(error);
    }
  }
);

// reducer for vehicle details report
export const vehicleDetailsSlice = createSlice({
  name: 'VehicleDetails',
  initialState: reportState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getVehicleDetails.pending, state => {
        state.isLoading = true;
        state.rows = [];
        state.columns = [];
      })
      .addCase(getVehicleDetails.fulfilled, (state, action) => {
        const {
          data: {
            data: { vehicleDetailReport, count, timeZone }
          }
        } = action.payload;

        state.isLoading = false;

        if (vehicleDetailReport && vehicleDetailReport.length > 0) {
          state.rows = vehicleDetailReport.map((item: any, index: number) => ({
            ...item,
            id: index + 1,
            vehicleNumber: item?.vehicleNumber.toUpperCase(),
            vehicleType: capitalizeFirstLetter(item?.vehicleType),
            vehicleModel: item?.vehicleModel,
            brand: item?.brand,
            subType: item?.subType,
            year: item?.makingYear,
            odometer: item?.odometer !== null ? item?.odometer?.toFixed(2) : 'N/A',
            mileage: item?.mileage,
            fuelCapacity: item?.fuelCapacity ?? 'N/A',
            gpsName: item?.gpsName === null ? 'N/A' : item?.gpsName,
            status: item?.status ? capitalizeFirstLetter(item?.status) : 'N/A',
            startDate: item?.startDate
              ? convertEpochToDateTime(item?.startDate, timeZone)
              : 'N/A',
            endDate: item?.endDate
              ? convertEpochToDateTime(item?.endDate, timeZone)
              : 'N/A'
          }));
          state.columns = vehicleDetailsColumns;
          state.count = count;
        }
      })
      .addCase(getVehicleDetails.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action.payload?.response?.data?.error ?? 'Something went wrong';
      });
  }
});

const reportDialogAction = createSlice({
  name: 'report/dialog',
  initialState: {
    isShowMap: false,
    Polyline: {},
    Waypoints: [],
    externalVehicle: null
  },
  reducers: {
    updateIsShowMap: (state, action) => {
      state.isShowMap = action.payload;
    },
    updatePolyline: (state, action) => {
      state.Polyline = action.payload;
    },
    updateWaypoints: (state, action) => {
      state.Waypoints = action.payload;
    },
    setExternalVehicle: (state: any, action) => {
      state.externalVehicle = action.payload;
    }
  }
});

//action for report pdf
export const getPDF = createAsyncThunk<ReturnedType, Payload>(
  'pdfURL',
  async (params, thunkApi) => {
    try {
      const { startDate, endDate, deviceID, report } = params;

      let URL = `${URLs.REPORT_PDF}`;

      if (deviceID) {
        URL += `reportType=${report}&deviceid=${deviceID}&format=PDF`;
      } else if (report === 'VehicleDetails' || report === 'DeviceHealth') {
        URL += `reportType=${report}&format=PDF`;
      } else {
        URL += `reportType=${report}&enddate=${endDate}&startdate=${startDate}&format=PDF`;
      }
      if (startDate && endDate) {
        URL += `&enddate=${endDate}&startdate=${startDate}`;
      }
      const response = await axiosInstance({
        method: 'GET',
        url: URL
      });
      if (response?.data?.data) {
        thunkApi.dispatch(
          updateToast({
            show: true,
            message: 'PDF Downloaded successfully',
            severity: 'success'
          })
        );
      }
      return response;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error.response?.data?.message,
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error);
    }
  }
);

export interface PDFprops {
  isLoading: boolean;
  pdfURL: string | null;
  error: string | null;
}

const pdfState: PDFprops = {
  isLoading: false,
  pdfURL: null,
  error: null
};

//reducer for report pdf
export const pdfSlice = createSlice({
  name: 'pdfURL',
  initialState: pdfState,
  reducers: {
    reset: () => pdfState
  },
  extraReducers: builder => {
    builder
      .addCase(getPDF.pending, state => {
        state.isLoading = true;
        state.pdfURL = null;
      })
      .addCase(getPDF.fulfilled, (state, action) => {
        const {
          data: { data: data }
        } = action.payload;
        state.isLoading = false;
        state.pdfURL = data;
      })
      .addCase(getPDF.rejected, (state, action: any) => {
        state.isLoading = false;
        state.pdfURL = null;
        state.error = action.payload.response?.data?.error || 'Something went wrong';
      });
  }
});

//action for report excel
export const getExcel = createAsyncThunk<ReturnedType, Payload>(
  'excelURL',
  async (params, thunkApi) => {
    try {
      const { startDate, endDate, deviceID, report } = params;
      let URL = `${URLs.REPORT_EXCEL}`;

      if (deviceID) {
        URL += `reportType=${report}&deviceid=${deviceID}&enddate=${endDate}&startdate=${startDate}&format=EXCEL`;
      } else if (report === 'VehicleDetails' || report === 'DeviceHealth') {
        URL += `reportType=${report}&format=EXCEL`;
      } else {
        URL += `reportType=${report}&enddate=${endDate}&startdate=${startDate}&format=EXCEL`;
      }
      const response = await axiosInstance({
        method: 'GET',
        url: URL
      });
      if (response?.data?.data) {
        thunkApi.dispatch(
          updateToast({
            show: true,
            message: 'Excel Downloaded successfully',
            severity: 'success'
          })
        );
      }
      return response;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error.response?.data?.message,
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error);
    }
  }
);

export interface Excelprops {
  isLoading: boolean;
  excelURL: string | null;
  error: string | null;
}

const excelState: Excelprops = {
  isLoading: false,
  excelURL: null,
  error: null
};

//reducer for report pdf
export const excelSlice = createSlice({
  name: 'excelURL',
  initialState: excelState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getExcel.pending, state => {
        state.isLoading = true;
        state.excelURL = null;
      })
      .addCase(getExcel.fulfilled, (state, action) => {
        const {
          data: { data: data }
        } = action.payload;
        state.isLoading = false;
        state.excelURL = data;
      })
      .addCase(getExcel.rejected, (state, action: any) => {
        state.isLoading = false;
        state.excelURL = null;
        state.error = action.payload.response?.data?.error || 'Something went wrong';
      });
  }
});

//action for driverscorecard
export const getDriverScorecardreport = createAsyncThunk<ReturnedType, Payload>(
  'DriverScorecard',
  async (params, thunkApi) => {
    const { startDate, endDate, toast, pageno, pagesize, signal } = params;
    try {
      let URL = `${URLs.DRIVER_SCORECARD}enddate=${endDate}&startdate=${startDate}&pageno=${pageno}&pagesize=${pagesize}`;

      const response = await axiosInstance({
        method: 'GET',
        url: URL,
        signal: signal
      });
      if (
        response.data.data.driverScorecardReportResponse &&
        response.data.data.driverScorecardReportResponse.length > 0
      ) {
        thunkApi.dispatch(
          updateToast({
            show: toast,
            severity: 'success',
            message: 'Driver Scorecard Report generated successfully'
          })
        );
      } else {
        thunkApi.dispatch(
          updateToast({
            show: true,
            severity: 'warning',
            message: 'No reports found'
          })
        );
      }
      return response;
    } catch (error: any) {
      if (error?.message !== 'canceled') {
        thunkApi.dispatch(
          updateToast({
            show: true,
            severity: 'error',
            message: error.response?.data?.message
          })
        );
      }
      return thunkApi.rejectWithValue(error);
    }
  }
);

//reducer for driver scorecard report
export const driverScorecardSlice = createSlice({
  name: 'DriverSCorecard',
  initialState: reportState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getDriverScorecardreport.pending, state => {
        state.isLoading = true;
        state.rows = [];
        state.columns = [];
      })
      .addCase(getDriverScorecardreport.fulfilled, (state, action) => {
        const {
          data: {
            data: { driverScorecardReportResponse, count }
          }
        } = action.payload;

        state.isLoading = false;

        if (driverScorecardReportResponse && driverScorecardReportResponse.length > 0) {
          state.rows = driverScorecardReportResponse.map((item: any, index: number) => ({
            ...item,
            id: index + 1,
            driverID: item?.driverID,
            driverName: item?.driverName,
            date: item?.updatetime,
            harshAcceleration: item?.harshAcceleration,
            drivercontact: item?.driverContactNumber,
            harshCornering: item?.harshCornering,
            harshBraking: item?.harshBraking,
            overSpeedDetails: item?.overSpeedDetails,
            driverScore: item?.driverScore
          }));
          state.columns = driverScorecardColumns;
          state.count = count;
        }
      })
      .addCase(getDriverScorecardreport.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action.payload?.response?.data?.error ?? 'Something went wrong';
      });
  }
});

//action for driver identification report
export const getDriverIdentificationreport = createAsyncThunk<ReturnedType, Payload>(
  'DriverIdentification',
  async (params, thunkApi) => {
    const { startDate, endDate, toast, pageno, pagesize, deviceID, signal } = params;
    try {
      let URL = `${URLs.IDENTIFICATION_REPORT}`;
      if (deviceID) {
        URL += `deviceid=${deviceID}&enddate=${endDate}&startdate=${startDate}&pageno=${pageno}&pagesize=${pagesize}`;
      } else {
        URL += `enddate=${endDate}&startdate=${startDate}&pageno=${pageno}&pagesize=${pagesize}`;
      }

      const response = await axiosInstance({
        method: 'GET',
        url: URL,
        signal: signal
      });
      if (
        response.data.data.driverIdentificationReportResponse &&
        response.data.data.driverIdentificationReportResponse.length > 0
      ) {
        thunkApi.dispatch(
          updateToast({
            show: toast,
            severity: 'success',
            message: 'Driver Identification Report generated successfully'
          })
        );
      } else {
        thunkApi.dispatch(
          updateToast({
            show: true,
            severity: 'warning',
            message: 'No reports found'
          })
        );
      }
      return response;
    } catch (error: any) {
      if (error?.message !== 'canceled') {
        thunkApi.dispatch(
          updateToast({
            show: true,
            severity: 'error',
            message: error.response?.data?.message
          })
        );
      }
      return thunkApi.rejectWithValue(error);
    }
  }
);

//slice for driver identification report
export const driverIdentificationSlice = createSlice({
  name: 'DriverIdentification',
  initialState: reportState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getDriverIdentificationreport.pending, state => {
        state.isLoading = true;
        state.rows = [];
        state.columns = [];
      })
      .addCase(getDriverIdentificationreport.fulfilled, (state, action) => {
        const {
          data: {
            data: { driverIdentificationReportResponse, count, timezone }
          }
        } = action.payload;
        state.isLoading = false;

        if (
          driverIdentificationReportResponse &&
          driverIdentificationReportResponse?.length > 0
        ) {
          state.rows = driverIdentificationReportResponse.map(
            (item: any, index: number) => ({
              ...item,
              id: index + 1,
              deviceID: item?.vehicleNumber.toUpperCase(),
              assigneddriverID: item?.assignedDriverId,
              assignedDriverName: item?.assignedDriverName,
              assignedDriverUuid: item?.assignedDriverUuid,
              currentDriverId: item?.currentDriverId,
              drivercontact: item?.driverContactNumber,
              currentDriverName: item?.currentDriverName,
              currentDriverUuid: item?.currentDriverUuid,
              eventName: item?.eventName,
              currentDriverContactNumber: item?.currentDriverContactNumber,
              assignedDriverContactNumber: item?.assignedDriverContactNumber,
              date: convertEpochToDateTime(item?.updateTime, timezone),
              lat: item?.latitude,
              lng: item?.longitude
            })
          );
          state.columns = driverIdentificationColumns;
          state.count = count;
        }
      })
      .addCase(getDriverIdentificationreport.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action.payload?.response?.data?.error ?? 'Something went wrong';
      });
  }
});

//action for trip compliance report
export const getTripComplianceReport = createAsyncThunk<ReturnedType, Payload>(
  'TripCompliance',
  async (params, thunkApi) => {
    const { startDate, endDate, toast, pageno, pagesize, deviceID, signal } = params;
    try {
      let URL = `${URLs.COMPLIANCE_REPORT}`;
      if (deviceID) {
        URL += `deviceid=${deviceID}&enddate=${endDate}&startdate=${startDate}&pageno=${pageno}&pagesize=${pagesize}`;
      } else {
        URL += `enddate=${endDate}&startdate=${startDate}&pageno=${pageno}&pagesize=${pagesize}`;
      }

      const response = await axiosInstance({
        method: 'GET',
        url: URL,
        signal: signal
      });
      if (
        response?.data?.data?.tripComplianceReportResponse &&
        response?.data?.data?.tripComplianceReportResponse?.length > 0
      ) {
        thunkApi.dispatch(
          updateToast({
            show: toast,
            message: 'Trip Compliance Report generated successfully',
            severity: 'success'
          })
        );
      } else {
        thunkApi.dispatch(
          updateToast({
            show: true,
            severity: 'warning',
            message: 'No reports found'
          })
        );
      }
      return response;
    } catch (error: any) {
      if (error?.message !== 'canceled') {
        thunkApi.dispatch(
          updateToast({
            show: true,
            severity: 'error',
            message: error.response?.data?.message
          })
        );
      }
      return thunkApi.rejectWithValue(error);
    }
  }
);

//reducer for trip compliance report
export const tripComplianceSlice = createSlice({
  name: 'TripCompliance',
  initialState: reportState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getTripComplianceReport.pending, state => {
        state.isLoading = true;
        state.rows = [];
        state.columns = [];
      })
      .addCase(getTripComplianceReport.fulfilled, (state, action) => {
        const {
          data: {
            data: { tripComplianceReportResponse, count, timezone }
          }
        } = action.payload;
        state.isLoading = false;

        if (tripComplianceReportResponse && tripComplianceReportResponse?.length > 0) {
          state.rows = tripComplianceReportResponse.map((item: any, index: number) => ({
            ...item,
            id: index + 1,
            deviceID: item?.vehicleNumber?.toUpperCase(),
            vehicleType: item?.vehicleType,
            driverID: item?.driverId,
            driverName: item?.driverName
              ? capitalizeFirstLetter(item?.driverName)
              : 'N/A',
            drivercontact: item?.driverContactNumber,
            tripID: item?.tripId,
            actualStartTime: convertEpochToDateTime(item?.actualTripStartTime, timezone),
            actualEndTime: convertEpochToDateTime(item?.actualTripEndTime, timezone),
            actualDuration: item?.actualDuration,
            actualDistance: item?.actualDistance.toFixed(2),
            assignedStartTime: convertEpochToDateTime(
              item?.assignedTripStartTime,
              timezone
            ),
            assignedEndTime: convertEpochToDateTime(item?.assignedTripEndTime, timezone),
            estimatedDuration: item?.assignedEstimatedDuration,
            estimatedDistance: item?.assignedEstimatedDistance.toFixed(2),
            source: item?.tripStartLocation,
            destination: item?.tripEndLocation,
            waypoints: item?.tripWayPointsDetails,
            idleDuration: item?.tripIdleDuration,
            parkingDuration: item?.tripParkingDuration,
            drivingDuration: item?.tripDrivingDuration,
            stoppageCount: item?.tripStoppageCount,
            tripCompliance: item?.overAllTripCompliancePercentage,
            plannedEncodedPolyline: item?.plannedEncodedPolyline,
            completedWaypoints: item?.tripComplianceDTO?.noOfCompletedWayPoints,
            incompleteWaypoiNts: item?.tripComplianceDTO?.noOfInCompleteWayPoints,
            actualRouteEncodedPolyline: item?.actualRouteEncodedPolyline,
            type: 'TripCompliance',
            timezone: timezone
          }));
          state.count = count;
          state.columns = tripComplianceColumns;
        }
      })
      .addCase(getTripComplianceReport.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action.payload?.response?.data?.error ?? 'Something went wrong';
      });
  }
});

//action for external vehicle report
export const getExternalVehicleReport = createAsyncThunk<any, any>(
  'externalvehicle',
  async (data, thunkApi) => {
    const { startDate, endDate, pageno, pagesize, toast } = data;
    try {
      const response = await axiosInstance({
        method: 'GET',
        url: `${URLs.EXTERNAL_VEHICLE_REPORT}fromEpoch=${startDate}&toEpoch=${endDate}&pageNo=${pageno}&pageSize=${pagesize}`
      });
      if (response?.data?.data?.vehicleDetails?.length > 0 && toast) {
        thunkApi.dispatch(
          updateToast({
            show: true,
            message: 'External Vehicle Report generated successfully',
            severity: 'success'
          })
        );
      } else {
        thunkApi.dispatch(
          updateToast({
            show: true,
            message: 'No reports found',
            severity: 'warning'
          })
        );
      }
      return response;
    } catch (err: any) {
      if (err?.message !== 'canceled') {
        thunkApi.dispatch(
          updateToast({
            show: true,
            message: err.response?.data?.message,
            severity: 'error'
          })
        );
      }
      return thunkApi.rejectWithValue(err);
    }
  }
);

//reducer for external vehicle report
export const externalVehicleReportSlice = createSlice({
  name: 'externalVehicle',
  initialState: reportState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getExternalVehicleReport.pending, state => {
        state.isLoading = true;
        state.rows = [];
        state.columns = [];
      })
      .addCase(getExternalVehicleReport.fulfilled, (state, action) => {
        state.isLoading = false;
        const {
          data: {
            data: { vehicleDetails, vehicleCount, timezone }
          }
        } = action.payload;
        if (vehicleDetails?.length > 0) {
          state.rows = vehicleDetails?.map((item: any, index: number) => ({
            ...item,
            id: index + 1,
            date: epochToDateFormatSg(item?.epochDate, timezone),
            vehicleNumber: item.vehicleNumber.toUpperCase(),
            noOfTrips: item?.tourDetails?.length,
            type: 'externalVehicle'
          }));
        }
        state.count = vehicleCount;
        state.columns = externalVehicleColumns;
      })
      .addCase(getExternalVehicleReport.rejected, (state: any, action: any) => {
        state.isLoading = false;
        state.error = action.payload?.response?.data?.message ?? 'Something went wrong';
      });
  }
});

//action for autoplanner reports  pdf and excel
export const getAutoplannerPDFExcel = createAsyncThunk<any, any>(
  'pdfExcel',
  async (data, thunkApi) => {
    const { startDate, endDate, report, mail, format } = data;
    let URL = `${URLs.AUTOPLANNER_PDF_EXCEL}fromEpoch=${startDate}&toEpoch=${endDate}&reportType=${format}&report=${report}`;
    if (mail) {
      URL += `&email=${mail}`;
    }
    try {
      const response = await axiosInstance({ method: 'GET', url: URL });
      if (response?.data?.data && !mail) {
        thunkApi.dispatch(
          updateToast({
            show: true,
            message: `${format === 'pdf' ? 'PDF' : 'Excel'} Downloaded successfully`,
            severity: 'success'
          })
        );
      } else if (mail) {
        thunkApi.dispatch(
          updateToast({
            show: true,
            message: response?.data?.message,
            severity: 'success'
          })
        );
      }
      return response;
    } catch (err: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: err?.response?.data?.message,
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(err);
    }
  }
);

//reducer for autoplanner reports pdf and excel
const getAutoplannerPDFExcelSlice = createSlice({
  name: 'pdfExcel',
  initialState: pdfExcelState,
  reducers: {
    clearReportURLS: state => {
      state.data = null;
    }
  },
  extraReducers: builder => {
    builder
      .addCase(getAutoplannerPDFExcel.pending, (state, action: any) => {
        state.isLoading = true;
        state.data = null;
      })
      .addCase(getAutoplannerPDFExcel.fulfilled, (state, action: any) => {
        state.isLoading = false;
        const {
          data: { data }
        } = action.payload;
        state.data = data;
      })
      .addCase(getAutoplannerPDFExcel.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action.payload?.response?.data?.message ?? 'Something went wrong';
      });
  }
});

export const devicesData = deviceListSlice.reducer;
export const kmReportsData = kmSlice.reducer;
export const stoppageReportsData = stoppageSlice.reducer;
export const engineReportsData = engineSlice.reducer;
export const idleReportsData = idleSlice.reducer;
export const movementReportsData = movementSlice.reducer;
export const parkingReportsData = parkingSlice.reducer;
export const overallReportsData = overallSlice.reducer;
export const overspeedReportsData = overspeedSlice.reducer;
export const alertReportsData = alertSlice.reducer;
export const detailedtripReportsData = detailedTripSlice.reducer;
export const deviceHealthReportsData = deviceHealthSlice.reducer;
export const vehicleGeofenceReportsData = vehicleGeofenceSlice.reducer;
export const vehicleDetailsReportsData = vehicleDetailsSlice.reducer;
export const reportDialog = reportDialogAction.reducer;
export const { updateIsShowMap, updatePolyline, updateWaypoints, setExternalVehicle } =
  reportDialogAction.actions;
export const pdfURL = pdfSlice.reducer;
export const excelURL = excelSlice.reducer;
export const driverScorecardReportsData = driverScorecardSlice.reducer;
export const getPDFExcelData = getPDFExcelSlice.reducer;
export const driverIdentificationReportsData = driverIdentificationSlice.reducer;
export const tripComplianceReportsData = tripComplianceSlice.reducer;
export const externalVehicleReportData = externalVehicleReportSlice.reducer;
export const autoplannerReportsPDFExcelData = getAutoplannerPDFExcelSlice.reducer;
export const { clearReportURLS } = getAutoplannerPDFExcelSlice.actions;
